import { forwardRef, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'

import { FaCaretDown, FaCaretUp, FaExchangeAlt } from 'react-icons/fa'
import { SiMarketo } from 'react-icons/si'
import { MdSettings } from 'react-icons/md'
import { CgMenuLeftAlt } from 'react-icons/cg'

import {
    Avatar,
    Body,
    DropBody,
    DropItem,
    Flex,
    Image,
    MHeaderButtonWrapper,
    MHeaderIconWrapper,
    MLink,
    MOBILE_SIZE,
    MText,
    Profile,
    RegisterIcon,
    SinginIcon,
} from './styles'
import {
    LayoutSettingIcon,
    LinkRow,
    Menu,
    Notification,
    UnreadNotificationIcon,
} from '../../styles/HeaderStyles'
import Text from '../../utils/Text'

import { useMainContext } from '../../contexts/main'
import { useWindowSize } from '../../hooks/common/useWindowSize'
import { useAuthContext } from '../../contexts/auth'
import { useUnreadNotificationQuery } from '../../services/react-query/useNotificationsQuery'
import useClickOutside from '../../hooks/common/useClickOutside'

import { HOME } from '../../constants/urls'

import NotificationDropdown from '../../modals/NotificationDropdown'
import SettingsDropdown from '../../modals/SettingsDropdown'
import { ProfileDropdown } from '../../modals/ProfileDropdown'

import LogoEn from '../../assets/images/logo-en.png'
import LogoFa from '../../assets/images/logo-fa.png'
import { TABLET_SIZE } from '../../constants/common'

export default function Header({ onLayoutSetting }) {
    const lsRef = useRef()
    const {
        main: { lang, setLang },
        profile: { token: isAuthed },
    } = useMainContext()
    const { width } = useWindowSize()
    const navigate = useNavigate()

    const [langOpen, setLangOpen] = useState(false)
    const [profileOpen, setProfileOpen] = useState(false)
    const [settingsOpen, setSettingsOpen] = useState(false)
    const [notificationsOpen, setNotificationsOpen] = useState(false)
    const [menuIsOpen, setMenuIsOpen] = useState(false)

    const langRef = useRef()
    useClickOutside(langRef, () => langOpen && setLangOpen(false))

    const profileRef = useRef()
    useClickOutside(profileRef, () => profileOpen && setProfileOpen(false))

    const notificationRef = useRef()
    useClickOutside(
        notificationRef,
        () => notificationsOpen && setNotificationsOpen(false)
    )

    const settingsRef = useRef()
    useClickOutside(settingsRef, () => settingsOpen && setSettingsOpen(false))

    const menuRef = useRef()
    useClickOutside(menuRef, () => menuIsOpen && setMenuIsOpen(false))

    const { profile } = useAuthContext()
    const {
        data: notifications,
        isFetching: notificationsLoading,
        refetch: refetchUnreadNotifications,
    } = useUnreadNotificationQuery(1)
    const { logout } = useAuthContext()

    const onOptionClicked = (type) => {
        setLangOpen(false)
        switch (type) {
            case 'english':
                setLang('en')
                break
            case 'فارسی':
                setLang('fa')
                break
            default:
                break
        }
    }

    const onProfileItemsClicked = (type, path = null) => {
        if (type === 'exit') {
            logout()
        } else {
            window.location.href = HOME + 'user' + path
        }
    }

    const onLinkClicked = (path) => {
        if (path !== '/markets' && path !== '/otc') {
            window.location.href = HOME + path
        } else {
            navigate(path)
        }
    }

    return (
        <Body
            resp={width < MOBILE_SIZE}
            className={'bg-banner-bg dark:bg-[banner-bg-dark] text-white'}>
            <AnimatePresence exitBeforeEnter>
                {menuIsOpen && (
                    <Menu
                        variants={menuVariants}
                        animate='in'
                        exit='out'
                        initial='out'
                        ref={menuRef}>
                        {getLinks(lang).map((link) => (
                            <LinkRow
                                key={link.path}
                                justify='flex-start'
                                onClick={() => onLinkClicked(link.path)}>
                                {link.icon}
                                <MLink key={link.title}>
                                    {link.external ? (
                                        <a href={link.path}>
                                            <Text tid={link.title} />
                                        </a>
                                    ) : (
                                        <Link to={link.path}>
                                            <Text tid={link.title} />
                                        </Link>
                                    )}
                                </MLink>
                            </LinkRow>
                        ))}
                    </Menu>
                )}
            </AnimatePresence>

            <div
                className={
                    'flex h-full items-center justify-between max-w-[1152px] mx-auto w-full'
                }>
                <Flex className={'h-full'}>
                    <a href={HOME}>
                        <img
                            src={lang === 'fa' ? LogoFa : LogoEn}
                            alt={'Walletino - Landing'}
                            width={lang === 'fa' ? 110 : 150}
                            height={40}
                            className={`object-contain`}
                        />
                    </a>
                    {width > MOBILE_SIZE &&
                        getLinks(lang).map((link) => (
                            <MLink key={link.title}>
                                {link.external ? (
                                    <a href={link.path}>
                                        <Text tid={link.title} />
                                    </a>
                                ) : (
                                    <Link to={link.path}>
                                        <Text tid={link.title} />
                                    </Link>
                                )}
                            </MLink>
                        ))}
                </Flex>

                <Flex>
                    {isAuthed ? (
                        <Flex
                            style={{
                                margin: '0 8px',
                                position: 'relative',
                                cursor: 'pointer',
                            }}
                            onClick={() => setNotificationsOpen(true)}>
                            <Notification size={20} />
                            {notifications?.data?.length ? (
                                <UnreadNotificationIcon />
                            ) : null}
                        </Flex>
                    ) : null}
                    {notificationsOpen && (
                        <NotificationDropdown
                            ref={notificationRef}
                            notifications={notifications}
                            loading={notificationsLoading}
                            onReadANotification={() =>
                                refetchUnreadNotifications()
                            }
                        />
                    )}

                    {width > TABLET_SIZE && (
                        <Flex ref={lsRef}>
                            <LayoutSettingIcon
                                size={20}
                                onClick={() => onLayoutSetting(lsRef)}
                            />
                        </Flex>
                    )}

                    {width > MOBILE_SIZE ? (
                        <>
                            <Flex ref={langRef}>
                                <MHeaderIconWrapper
                                    onClick={() =>
                                        setLangOpen((state) => !state)
                                    }>
                                    <Flex>
                                        {langOpen ? (
                                            <FaCaretUp
                                                color='#c3c5b7'
                                                size={18}
                                            />
                                        ) : (
                                            <FaCaretDown
                                                color='#c3c5b7'
                                                size={18}
                                            />
                                        )}
                                        <MText>{languages[lang]}</MText>
                                    </Flex>
                                </MHeaderIconWrapper>
                                {langOpen && (
                                    <Dropdown
                                        options={languageOptions}
                                        onOptionClicked={onOptionClicked}
                                        active={
                                            lang === 'en' ? 'english' : 'فارسی'
                                        }
                                    />
                                )}
                            </Flex>
                        </>
                    ) : (
                        <Flex
                            style={{
                                margin: width > MOBILE_SIZE ? '0 5px' : '0 2px',
                            }}>
                            <MdSettings
                                onClick={() => setSettingsOpen(true)}
                                size={20}
                                color='#c3c5b7'
                            />
                            {settingsOpen && (
                                <SettingsDropdown ref={settingsRef} />
                            )}
                        </Flex>
                    )}

                    {isAuthed ? (
                        <Profile
                            onClick={() => setProfileOpen((state) => !state)}
                            size={width < MOBILE_SIZE ? '34px' : '42px'}>
                            {profile?.avatar ? (
                                <Image
                                    src={profile?.avatar}
                                    alt=' '
                                    size={width < MOBILE_SIZE ? '34px' : '42px'}
                                />
                            ) : (
                                <Avatar size={width < MOBILE_SIZE ? 18 : 22} />
                            )}
                            {profileOpen && (
                                <ProfileDropdown
                                    ref={profileRef}
                                    width={width}
                                    profile={profile}
                                    onProfileItemsClicked={
                                        onProfileItemsClicked
                                    }
                                />
                            )}
                        </Profile>
                    ) : (
                        <>
                            <MHeaderIconWrapper>
                                <a
                                    href={HOME + `user/register-signin?tab=0`}
                                    className='block'>
                                    <Flex>
                                        <RegisterIcon size={18} />
                                        <MText>
                                            <Text tid='register' />
                                        </MText>
                                    </Flex>
                                </a>
                            </MHeaderIconWrapper>
                            <MHeaderButtonWrapper>
                                <a
                                    href={HOME + `user/register-signin?tab=1`}
                                    className='block'>
                                    <Flex>
                                        <SinginIcon size={18} />
                                        <Text tid='signin' />
                                    </Flex>
                                </a>
                            </MHeaderButtonWrapper>
                        </>
                    )}
                    {width < MOBILE_SIZE && (
                        <CgMenuLeftAlt
                            onClick={() => setMenuIsOpen((state) => !state)}
                            size={28}
                            color='#c3c5b7'
                        />
                    )}
                </Flex>
            </div>
        </Body>
    )
}

const Dropdown = forwardRef((props, ref) => {
    const { options, active, onOptionClicked } = props

    return (
        <DropBody ref={ref}>
            {options.map((option) => (
                <DropItem
                    key={option}
                    onClick={() => onOptionClicked(option)}
                    active={active === option}>
                    <Flex style={{ padding: '0 20px' }}>{option}</Flex>
                </DropItem>
            ))}
        </DropBody>
    )
})

const getLinks = (lang) => [
    {
        title: 'otc',
        path: HOME + lang + '/otc2/form',
        icon: <FaExchangeAlt size={18} />,
        external: true,
    },
    {
        title: 'markets',
        path: HOME + lang + '/market',
        icon: <SiMarketo size={18} />,
        external: true,
    },
    { title: 'p2p-trade', path: '/', icon: <SiMarketo size={18} /> },
]

const languages = {
    en: 'english',
    fa: 'فارسی',
}

const languageOptions = ['english', 'فارسی']

const menuVariants = {
    in: {
        x: 0,
        transition: { duration: 0.4 },
    },
    out: {
        x: -300,
        transition: { duration: 0.4 },
    },
}
